"use client"

import clsx from "clsx"
import React, { forwardRef, useState } from "react"
import { Text } from "./Text"
import { TextVariant } from "@/theme"
import { cn } from "@/utils/classnames"

type TextInputVariant = "underline" | "grey-fill" | "white-fill"

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  loading?: boolean
  variant?: TextInputVariant
  wrapperClassname?: string
  labelVariant?: TextVariant
  labelClassName?: string
}

const underlineClasses = {
  input: "border-b bg-bg-1 bg-transparent py-2",
  wrapper: null,
}
const greyFillClasses = {
  input: null,
  wrapper: "bg-bg-3 rounded-full px-3 min-h-[40px] flex flex-row items-center",
}
const whiteFillClasses = {
  input: null,
  wrapper: "bg-bg-1 rounded-full px-3 min-h-[40px] flex flex-row items-center",
}

export const TextInput = forwardRef<HTMLInputElement, Props>(
  ({ label, className, variant, wrapperClassname, labelVariant, labelClassName, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false)
    const { onBlur, onFocus } = props

    const handleFocus = (e: any) => {
      setIsFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e: any) => {
      setIsFocused(false)
      onBlur?.(e)
    }

    let variantClasses
    switch (variant) {
      case "underline":
        variantClasses = underlineClasses
        break
      case "grey-fill":
        variantClasses = greyFillClasses
        break
      case "white-fill":
        variantClasses = whiteFillClasses
        break
      default:
        variantClasses = underlineClasses
    }

    return (
      <div className={wrapperClassname || "w-full"}>
        {label && (
          <Text variant={labelVariant || "label"} className={cn("mb-2", labelClassName)}>
            {label}
          </Text>
        )}
        <div className={clsx("w-full", variantClasses.wrapper)}>
          <input
            type="text"
            {...props}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={ref}
            className={clsx(
              "w-full border-b border-l-0 border-r-0 border-t-0 p-0 text-body1-md focus:outline-none focus:ring-0 lg:text-body1-lg",
              isFocused ? "border-primary-3" : "border-bg-3",
              variantClasses.input,
              className
            )}
          />
        </div>
      </div>
    )
  }
)

TextInput.displayName = "TextInput"
